import { useCallback, useMemo, useState } from 'react';
import type { AuditLogRow } from '../../../shell/audit/auditLogs';
import { categoryToFilter, type AuditCategory } from '../../../shell/audit/auditCategories';

export type AuditPeriodPreset = 'today' | '7d' | '30d' | '90d' | 'custom';

export const AUDIT_PERIOD_PRESETS: Array<{ id: AuditPeriodPreset; label: string; days?: number }> = [
  { id: 'today', label: 'Hoy', days: 0 },
  { id: '7d', label: '7 días', days: 7 },
  { id: '30d', label: '30 días', days: 30 },
  { id: '90d', label: '90 días', days: 90 },
  { id: 'custom', label: 'Personalizado' },
];

export type AuditCategoryFilter = AuditCategory | 'all';

export type TeamHistoryFilters = {
  category: AuditCategoryFilter;
  setCategory: (value: AuditCategoryFilter) => void;
  periodPreset: AuditPeriodPreset;
  setPeriodPreset: (value: AuditPeriodPreset) => void;
  customFromDate: string;
  setCustomFromDate: (value: string) => void;
  customToDate: string;
  setCustomToDate: (value: string) => void;
  userFilter: string | null;
  setUserFilter: (value: string | null) => void;
  hasActiveFilters: boolean;
  clearFilters: () => void;
  filteredRows: (rows: AuditLogRow[]) => AuditLogRow[];
  /** Filters to send to the server (userId + category filter string). */
  serverFilters: { userId: string | null; categoryFilter: string | null };
};

function isoToday(): string {
  return new Date().toISOString().slice(0, 10);
}

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function resolvePeriodRange(preset: AuditPeriodPreset, customFrom: string, customTo: string): { from: string; to: string } | null {
  if (preset === 'custom') {
    if (!customFrom || !customTo) return null;
    return { from: customFrom, to: customTo };
  }
  const match = AUDIT_PERIOD_PRESETS.find((p) => p.id === preset);
  const days = match?.days ?? 30;
  return { from: isoDaysAgo(days), to: isoToday() };
}

export type TeamHistoryPeriodRange = {
  fromDate: string;
  toDate: string;
  fromIso: string;
  toIso: string;
};

export type TeamHistoryFiltersWithRange = TeamHistoryFilters & {
  periodRange: TeamHistoryPeriodRange | null;
};

export function useTeamHistoryFilters(
  defaultPeriodPreset: AuditPeriodPreset = 'today',
): TeamHistoryFiltersWithRange {
  const defaultDays = AUDIT_PERIOD_PRESETS.find((p) => p.id === defaultPeriodPreset)?.days ?? 0;
  const [category, setCategory] = useState<AuditCategoryFilter>('all');
  const [periodPreset, setPeriodPreset] = useState<AuditPeriodPreset>(defaultPeriodPreset);
  const [customFromDate, setCustomFromDate] = useState(isoDaysAgo(defaultDays));
  const [customToDate, setCustomToDate] = useState(isoToday());
  const [userFilter, setUserFilter] = useState<string | null>(null);

  const periodRange = useMemo((): TeamHistoryPeriodRange | null => {
    const range = resolvePeriodRange(periodPreset, customFromDate, customToDate);
    if (!range) return null;
    return {
      fromDate: range.from,
      toDate: range.to,
      fromIso: `${range.from}T00:00:00.000Z`,
      toIso: `${range.to}T23:59:59.999Z`,
    };
  }, [periodPreset, customFromDate, customToDate]);

  const hasActiveFilters =
    category !== 'all' ||
    periodPreset !== defaultPeriodPreset ||
    userFilter != null;

  const clearFilters = useCallback(() => {
    setCategory('all');
    setPeriodPreset(defaultPeriodPreset);
    setCustomFromDate(isoDaysAgo(defaultDays));
    setCustomToDate(isoToday());
    setUserFilter(null);
  }, [defaultPeriodPreset, defaultDays]);

  /** Category and user filters are now applied server-side; filteredRows is a passthrough. */
  const filteredRows = useCallback(
    (rows: AuditLogRow[]): AuditLogRow[] => rows,
    [],
  );

  const serverFilters = useMemo(
    () => ({
      userId: userFilter,
      categoryFilter: category !== 'all' ? categoryToFilter(category) : null,
    }),
    [userFilter, category],
  );

  return {
    category,
    setCategory,
    periodPreset,
    setPeriodPreset,
    customFromDate,
    setCustomFromDate,
    customToDate,
    setCustomToDate,
    userFilter,
    setUserFilter,
    hasActiveFilters,
    clearFilters,
    filteredRows,
    serverFilters,
    periodRange,
  };
}
